import Icon from "@/Components/Icon/Icon";
import Spinner from "@/Components/Spinner/Spinner";
import Tag from "@/Components/Tag/Tag";
import { selectedGarnishAtom } from "@/shared/atoms";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useOnScreenHook from "../../shared/hooks/useOnScreenHook";
import { getFieldByLang, includesSearch } from "../../shared/utils/helpers";
import IconButton from "../IconButton/IconButton";
import Search from "../Input/Search";
import ToppingList from "./ToppingList";
import { Garnish, Topping } from "@/shared/utils/interfaces";
import { UseQueryResult } from "@tanstack/react-query";
import useVatHook from "@/shared/hooks/useVatHook";

const itemCount = 10;

interface Props {
  garnishQuery: UseQueryResult<Garnish[], Error>;
  toppingQuery: UseQueryResult<Topping[], Error>;
}

const Toppings = ({ toppingQuery, garnishQuery }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [create, setCreate] = useState(false);
  const { data: toppings, isPending, refetch: refetchTopping } = toppingQuery;
  const { refetch: refetchGarnish } = garnishQuery;

  const [containerRef, isVisible] = useOnScreenHook({});
  const [pageCount, setPageCount] = useState(0);
  const [showArr, setShowArr] = useState(
    toppings?.slice(itemCount * pageCount, itemCount * (pageCount + 1))
  );
  const [selectedGarnish, setSelectedGarnish] = useAtom(selectedGarnishAtom);
  const { vats } = useVatHook();

  useEffect(() => {
    setShowArr(toppings?.slice(0, itemCount * (pageCount + 1)));
  }, [toppings]);

  useEffect(() => {
    if (isVisible) {
      const newPageCount = pageCount + 1;
      const newArr = toppings?.slice(
        itemCount * newPageCount,
        itemCount * (newPageCount + 1)
      );
      setPageCount(newPageCount);
      setShowArr((prev) => [...prev, ...newArr]);
    }
  }, [isVisible]);

  const refetch = () => {
    refetchTopping();
    refetchGarnish();
  };

  return (
    <div className="mt-[4rem]">
      <div className="flex">
        <Search
          onDeleteClick={() => setSearch("")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("menu.toppingSearch")}
        />
        <div className="ml-[2rem]">
          <IconButton
            onClick={() => setCreate(!create)}
            variants={create ? "times" : "plus"}
            variant={create ? "danger" : "success"}
          />
        </div>
      </div>
      {selectedGarnish?.name && (
        <div className="mt-[2rem]">
          <Tag>
            <div
              className="flex items-center gap-4"
              onClick={() => setSelectedGarnish({})}
              style={{ cursor: "pointer" }}
            >
              {getFieldByLang(selectedGarnish.name)}
              <Icon className="h-8 w-8 fill-white" variants="times" />
            </div>
          </Tag>
        </div>
      )}
      {create && (
        <div className="pb-[2rem] mt-[2rem] border-b-[1px_solid] border-b-[grey]">
          <div className="font-semibold text-[2rem]">
            {t("menu.extraCreateMessage")}
          </div>
          <div className="overflow-x-auto">
            <ToppingList
              topping={{
                description: { all: "" },
                price: "",
                code: "",
                name: { all: "" },
                is_active: true,
                vat_enum: vats[1].id,
                item_type: "4",
              }}
              refetch={() => refetchTopping()}
              setCreate={setCreate}
            />
          </div>
        </div>
      )}
      {isPending ? (
        <div className="mt-8 flex justify-center">
          <Spinner size="0.5rem" />
        </div>
      ) : (
        <div className="max-h-[42rem] overflow-x-auto overflow-y-auto mt-[2rem]">
          {selectedGarnish?.sub_content
            ? selectedGarnish?.sub_content?.map((t) => (
                <ToppingList
                  topping={t}
                  key={t.code}
                  refetch={() => refetch()}
                  hasSelectedGarnish
                />
              ))
            : search !== ""
              ? toppings
                  ?.filter((topping) => includesSearch(topping, search))
                  ?.map((topping) => (
                    <ToppingList
                      topping={topping}
                      key={topping.item_id}
                      refetch={() => refetch()}
                    />
                  ))
              : showArr?.map((topping, index) => (
                  <div key={topping.item_id}>
                    <ToppingList topping={topping} refetch={() => refetch()} />
                    {showArr.length - 1 === index &&
                      toppings?.length - 1 !== index && (
                        // @ts-ignore
                        <div ref={containerRef} className="h-[50px]">
                          {t("menu.loading")}
                        </div>
                      )}
                  </div>
                ))}
        </div>
      )}
    </div>
  );
};

export default Toppings;
